import { type Opportunity } from "@entities";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { hasNoCoordinates } from "@shared/helpers/geo";
import { type FC, useMemo, useState } from "react";
import { ADMIN_TABLE_BADGE_GROUP_PARENT, ADMIN_TABLE_BADGE_GROUP_INSTANCE } from "@shared/helpers/opportunity-group-admin-copy";
import { type CustomCell } from "../../../pages/tables/users-table/volunteers/components/types";
import { useUser } from "../../../contexts/UserProvider";
import { CallCenterVolunteerAssignmentMark } from "../../call-center/CallCenterVolunteerAssignmentMark";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const INDICATOR_COLOR = "#5a7d9a";

function isLastCallApproachingOrPassed(opp: Opportunity): boolean {
  const last = opp.lastCallToVolunteers;
  if (!last) return false;
  const lastDate = new Date(last).getTime();
  return lastDate <= Date.now() + ONE_DAY_MS;
}

function getIndicators(opp: Opportunity): { id: string; label: string; icon: React.ReactNode; }[] {
  const list: { id: string; label: string; icon: React.ReactNode; }[] = [];
  if (opp.isSensitive) {
    list.push({
      id: "sensitive",
      label: "משימה רגישה",
      icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: INDICATOR_COLOR }} />,
    });
  }
  const interested = opp.interestedCount ?? 0;
  if (interested > 0) {
    list.push({
      id: "interested",
      label: `מתעניינים: ${interested}`,
      icon: <PeopleOutlineRoundedIcon sx={{ fontSize: 18, color: INDICATOR_COLOR }} />,
    });
  }
  if (hasNoCoordinates(opp.coordinates)) {
    list.push({
      id: "no-coords",
      label: "אין קואורדינטות",
      icon: <LocationOffOutlinedIcon sx={{ fontSize: 18, color: INDICATOR_COLOR }} />,
    });
  }
  if (opp.dailyVolunteerTargetReached) {
    list.push({
      id: "daily-target",
      label: "היעד היומי הושלם",
      icon: <PeopleOutlineRoundedIcon sx={{ fontSize: 18, color: INDICATOR_COLOR }} />,
    });
  }
  if (isLastCallApproachingOrPassed(opp)) {
    const last = opp.lastCallToVolunteers ? new Date(opp.lastCallToVolunteers) : null;
    const isPast = last ? last.getTime() < Date.now() : false;
    list.push({
      id: "last-call",
      label: isPast ? "מועד אחרון להרשמה עבר" : "מועד אחרון להרשמה מתקרב (ביממה הקרובה)",
      icon: <ScheduleIcon sx={{ fontSize: 18, color: INDICATOR_COLOR }} />,
    });
  }
  return list;
}

const OpportunityTitle: FC<
  CustomCell<Opportunity> & { poolTone?: "avoid" | "assigned" }
> = ({ row, poolTone }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { currentUser } = useUser();
  const opp = row.original;
  const assignedToCallCenter = !!opp.callCenterInVolunteerPool;

  const indicators = useMemo(
    () => getIndicators(opp),
    [
      opp.isSensitive,
      opp.interestedCount,
      opp.coordinates,
      opp.lastCallToVolunteers,
      opp.dailyVolunteerTargetReached,
    ],
  );

  const number =
    opp.isOpportunityGroupParent || opp.participantsCount <= 0
      ? null
      : opp.amount / opp.participantsCount;
  const color =
    number == null
      ? "grey.400"
      : number > 2 && !opp.isFull
        ? "red"
        : number > 1 && !opp.isFull
          ? "orange"
          : "green";

  const showGroupBadge = opp.isOpportunityGroupParent;
  const showInstanceBadge = Boolean(opp.parentOpportunityId);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      justifyContent="center"
      gap={0.75}
      padding="0.65rem 0"
      width="100%"
    >
      <Stack direction="row" gap={0.75} alignItems="center" flex={1} minWidth={0}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: color,
          }}
        />
        {indicators.length > 0 && (
          <>
            <Box
              component="button"
              type="button"
              onClick={handleClick}
              aria-label={`${indicators.length} חיוויים`}
              aria-describedby={open ? "opportunity-indicators-popover" : undefined}
              sx={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 16,
                height: 16,
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
                color: "text.secondary",
                fontSize: "0.65rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                lineHeight: 1,
                "&:hover": {
                  bgcolor: "action.selected",
                  borderColor: "primary.main",
                  color: "primary.main",
                },
              }}
            >
              {indicators.length}
            </Box>
            <Popover
              id="opportunity-indicators-popover"
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              slotProps={{ paper: { elevation: 8 } }}
              onClick={(e) => e.stopPropagation()}
            >
              <Paper variant="outlined" sx={{ overflow: "hidden", minWidth: 280 }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    חיוויים
                  </Typography>
                </Box>
                <List dense disablePadding sx={{ py: 0.5 }}>
                  {indicators.map((item) => (
                    <ListItem key={item.id} disablePadding sx={{ px: 1.5, py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 28, color: "inherit" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Popover>
          </>
        )}
        {showGroupBadge && (
          <Chip
            label={ADMIN_TABLE_BADGE_GROUP_PARENT}
            size="small"
            variant="outlined"
            sx={{
              flexShrink: 0,
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          />
        )}
        {showInstanceBadge && (
          <Chip
            label={ADMIN_TABLE_BADGE_GROUP_INSTANCE}
            size="small"
            variant="outlined"
            color="primary"
            sx={{
              flexShrink: 0,
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          />
        )}
        {opp.isLongTerm && (
          <Chip
            label="התנדבות ארוכת טווח"
            size="small"
            variant="outlined"
            sx={{
              flexShrink: 0,
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          />
        )}
        <Typography
          fontSize="0.9rem"
          component="span"
          title={opp.title}
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {opp.title}
        </Typography>
      </Stack>
      {assignedToCallCenter && (
        <CallCenterVolunteerAssignmentMark
          forStation={
            (poolTone ??
              (currentUser?.isCallCenterStation ? "assigned" : "avoid")) ===
            "assigned"
          }
        />
      )}
    </Stack>
  );
};

export default OpportunityTitle;
